import _ from 'lodash';
import { Model } from './Model';
import { Guest } from './Guest';
import { Member } from './Member';
import { Membership } from './Membership';
import { Swipe } from './Swipe';
// import { log } from '../log';

export class Session extends Model {

  /**
   * @property id
   *
   * The Session ID.
   *
   * @type {string}
   */

  /**
   * @property swipe
   *
   * The Swipe event that initiated the interaction with this session.
   * This is needed internally to implement some of the `send`
   * functionality, but should not be needed otherwise.
   *
   * @type {Swipe}
   */

  /**
   * @property membershipId
   *
   * The membership id associated with this session.
   *
   * @type {string}
   */
  get membershipId() { return this.id.split( '@' )[1]; }

  /**
   * @property membership
   *
   * The membership associated with this session.
   *
   * @type {Membership}
   */

  /**
   * @property members
   *
   * Member records of members associated with the membership of this
   * session..
   *
   * @type {Member[]}
   */

  /**
   * @property friends
   *
   * Member records of friends that are associated with this session.
   *
   * @type {Member[]}
   */

  /**
   * @property guests
   *
   * Guest records of guests that are associated with this session.
   *
   * @type {Guest[]}
   */

  /**
   * @property events
   *
   * Events of this session.
   *
   * @type {object[]}
   */

  constructor( options={} ) {
    super( {} );
    this.rebuild( options );
  }

  rebuild( options={} ) {
    const { members, friends, guests, ...opts } = options;
    _.assign( this, opts );
    this.members = _.map( members, member => {
      return new Member( { ...member, relation : 'family' } );
    } );
    this.friends = _.map( friends, friend => {
      return new Member( { ...friend, relation : 'friend' } );
    } );
    this.guests = _.map( guests, guest => {
      return new Guest( { ...guest, relation : 'guest' } );
    } );
    _.each( this.people, person => {
      if ( ! person.displayName ) {
        // TODO - Make the options for what populates displayName
        // configurable
        person.displayName = person.firstName || person.name;
      }
    } );
  }

  get people() {
    return [].concat( this.members, this.friends, this.guests );
  }

  getMember( id ) { return _.find( this.members, { id } ); }
  getFriend( id ) { return _.find( this.friends, { id } ); }
  getGuest( id ) { return _.find( this.guests, { id } ); }

  getPerson( id ) {
    return this.getMember( id )
      || this.getFriend( id )
      || this.getGuest( id );
  }

  /**
   * Determine whether a given member is currently signed in or not.
   *
   * @param {string} id - The ID to check (Member ID or Guest ID).
   */
  isPresent( id ) {
    if ( _.isObject( id ) && id.id ) id = id.id;
    const person = this.getPerson( id );
    return person && person.present;
  }

  findEvent( query ) { return _.find( this.events, query ); }
  findLastEvent( query ) { return _.findLast( this.events, query ); }
}

/*

Sessions represent the comings and goings of a particular membership
at a particular point in time.

Sessions are stored in NDJSON as a series of events:

    { event : 'member-in', memberId, time, ident }

Events that may be included:

 - member-in        - Someone added themselves to a session
 - member-out       - Someone removed themselves from a session
 - family-in        - Someone added a family member to a session
 - family-out       - Someone removed a family member from a session
 - friend-in        - Someone added a guest to a session
 - friend-out       - Someone removed a guest from a session
 - guest-in         - Someone added a guest to a session
 - guest-out        - Someone removed a guest from a session

Common event properties:
 - event           - Type of event
 - time            - Timestamp of event
 - card            - Card that was swiped to initiate an event
 - station         - Station where the event happened
 - ident           - Identity of the person who initiated the event
 - ident.memberId  - The Member ID of the member that initiated the event
 - ident.card      - The card that identified the member

NOTES:
 - It's possible for the `ident.memberId` field of an event to not be
   a member of the associated membership, if they were signed in or
   out by a staff member, for example.  It's also possible for the
   `ident.memberId` field to be `SYSTEM` if they were signed out by
   a system activity (for example, when everyone is automatically
   signed out by a `facility-closed` event).

*/
